// The sum on the door (roadmap 27): the score of one reveal, built step by step. Display only: every number comes from
// the reveal message (points, mult, double, special, half, and a streak once the server sends one); nothing is scored here.
import { MULTIPLIERS, DOORKEEPER_POINTS, ONE_KEY } from '../game/rules.js';
import { litOf, LIT_KNOCKS } from './neighbours-lit.js';

export const SUM_FIRST_MS = 250, SUM_STEP_MS = 420;
const BEASTS = new Set(['bull', 'bear']);
const near = (a, b) => Math.abs(a - b) < 1e-6;
const num = n => String(+n.toFixed(2));

const total = pts => ({ kind: 'total', text: `= ${pts}`, value: pts });

// Steps in time order: base, the special, the streak (item 3: m.streak > 1), the outbreak, an AI doorkeeper's half.
// Each is { kind, text, special? }; only the special's step carries `special` (it is the one coloured step).
// The last step is always `= points`: a reveal the steps cannot explain shows just that.
export function sumSteps(m, role) {
  const pts = m.points ?? 0;
  if (!pts) return [total(0)];
  const mult = m.mult > 1 ? m.mult : 1, beast = BEASTS.has(m.special);
  // One Key (game/rules.js ONE_KEY): To shelter is half a call, and the key's Open counts x2 when no bigger special is on the knock
  const away = m.verdict === 'away' ? ONE_KEY.away : 0;
  const base = role === 'visitor' ? 100 : away ? (pts > 0 ? away : -away) : pts > 0 ? DOORKEEPER_POINTS.right : DOORKEEPER_POINTS.wrong;
  const streak = m.streak > 1 ? m.streak : 1, outbreak = m.double && !beast ? MULTIPLIERS.outbreak : 1;
  const rest = mult / streak / outbreak;
  const steps = [{ kind: 'base', text: String(base) }];
  if (rest > 1 && m.keyOpen && near(rest, Math.max(ONE_KEY.mult, MULTIPLIERS[m.special] ?? 1)) && !(MULTIPLIERS[m.special] > ONE_KEY.mult)) {
    steps.push({ kind: 'special', special: null, text: `x ${ONE_KEY.mult} KEY` });
  } else if (rest > 1) {
    if (!MULTIPLIERS[m.special] || m.special === 'outbreak' || !near(rest, MULTIPLIERS[m.special])) return [total(pts)];
    // a lit knock (neighbours-lit.js) scores as the bull; its step wears the look's word and light (x 2 PINCH)
    const litLook = litOf(m.special, m.look);
    steps.push({ kind: 'special', special: litLook ?? m.special, text: `x ${MULTIPLIERS[m.special]} ${litLook ? LIT_KNOCKS[litLook].word : m.special.toUpperCase()}` });
  } else if (!near(rest, 1)) return [total(pts)];
  if (streak > 1) steps.push({ kind: 'streak', text: `x ${num(streak)} STREAK` });
  if (outbreak > 1) steps.push({ kind: 'outbreak', text: `x ${outbreak} OUTBREAK` });
  let made = base * mult;
  if (m.half) { steps.push({ kind: 'half', text: 'x 0.5 HALF' }); made = Math.trunc(made / 2); }
  if (!near(made, pts)) return [total(pts)];
  steps.push(total(pts));
  return steps;
}

// How many of n steps are up t ms after the sum began; reduced motion shows them all at once.
export const stepsShown = (n, t, reduced = false) => (reduced ? n : t < SUM_FIRST_MS ? 0 : Math.min(n, 1 + Math.floor((t - SUM_FIRST_MS) / SUM_STEP_MS)));
export const sumEndMs = n => SUM_FIRST_MS + (n - 1) * SUM_STEP_MS;

// The line a knock's gold tells each side. Gold doubles a doorkeeper's call, right or wrong (game/rules.js MULTIPLIERS.gold);
// it never pays a knocker (a streak can, so the line must not say "plain").
export const goldLine = role => (role === 'doorkeeper'
  ? `A gold knock. Your call counts ${MULTIPLIERS.gold === 2 ? 'double' : `x${MULTIPLIERS.gold}`}, right or wrong.`
  : 'A gold knock. Gold pays the doorkeeper, not you: it adds nothing to your points.');

// The shift total in the corner: it counts up from the old total to the new over countMs after each reveal's sum.
// key: the shift the reveal belongs to (a new one starts again at zero). seed: the board's number, before any reveal.
export function createShiftTotal({ countMs = 600, reduced = false } = {}) {
  let key = null, from = 0, to = 0, at = 0, adds = 0;
  const api = {
    reduced,
    get target() { return to; },
    value(now) {
      if (api.reduced || now >= at + countMs) return to;
      return now <= at ? from : Math.round(from + (to - from) * ((now - at) / countMs));
    },
    add(points, now, shift = null) {
      if (key !== null && shift !== key) from = to = 0;
      key = shift;
      from = api.value(now); to += points; at = now; adds++;
    },
    seed(points) { if (!adds) from = to = points; },
    reset() { from = to = at = adds = 0; key = null; },
  };
  return api;
}
